import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import { UnifiedThemeProvider } from '@backstage/theme';
import { StylesProvider } from '@material-ui/core/styles';
import { honeTheme, honeDarkTheme } from '../../theme/honeTheme';
import { createStableClassNameGenerator } from '../../theme/stableClassNames';

/*
 * `UnifiedThemeProvider` installs a JSS generator that minifies `makeStyles`
 * class names in production (`jss4-*`), which breaks every `[class*='Backstage…']`
 * selector in `hone-portal.css`. Nesting a `StylesProvider` INSIDE it — MUI uses
 * the nearest ancestor — swaps in a generator that keeps the readable names in
 * all environments. See `theme/stableClassNames.ts`.
 */
const stableClassNameGenerator = createStableClassNameGenerator();

/*
 * Replaces Backstage's stock light and dark themes. `plugin-app` registers its
 * own as `ThemeBlueprint.make({ name: 'light' | 'dark' })` under plugin `app`,
 * giving them the ids `theme:app/light` and `theme:app/dark`; an extension with
 * the same id in an `app` module takes its place, so the portal themes become
 * THE light and dark themes rather than extra entries in the theme picker.
 *
 * Both variants are registered, each wrapped in the same `StylesProvider`
 * pattern (see the comment above `stableClassNameGenerator`). With both
 * present and no forced preference, Backstage's theme picker defaults to
 * Auto, which follows the OS `prefers-color-scheme`.
 */
const honeLightTheme = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light',
      title: 'Hone Light',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={honeTheme}>
          <StylesProvider generateClassName={stableClassNameGenerator}>
            {children}
          </StylesProvider>
        </UnifiedThemeProvider>
      ),
    },
  },
});

const honeDarkThemeExtension = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark',
      title: 'Hone Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={honeDarkTheme}>
          <StylesProvider generateClassName={stableClassNameGenerator}>
            {children}
          </StylesProvider>
        </UnifiedThemeProvider>
      ),
    },
  },
});

export const themeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [honeLightTheme, honeDarkThemeExtension],
});
